import { AlignedRecordings } from '../types/clip-types';
import { TimeSpan } from '../types/video-feed-types';
import { isEntitySharedVideo } from './activityUtils';
import { mergeWindows, subtractRanges } from './rangeUtils';

type TimeRange = [number, number];

export interface SharedVideoSubtractionContext {
  sharedVideoSpeakers: Set<string>;
  mergedSharedVideoRanges: TimeRange[];
}

export const getSharedVideoSubtractionContext = (
  alignedRecordings: AlignedRecordings[]
): SharedVideoSubtractionContext => {
  const sharedVideos = alignedRecordings.filter(recording => isEntitySharedVideo(recording.entity));

  if (sharedVideos.length === 0) {
    return {
      sharedVideoSpeakers: new Set<string>(),
      mergedSharedVideoRanges: []
    };
  }

  const sharedVideoSpeakers = new Set<string>(
    sharedVideos
      .map(recording => (recording.transcript_mapping as Record<string, string[]>)?.speaker_labels?.[0])
      .filter((speakerKey): speakerKey is string => typeof speakerKey === 'string' && speakerKey.length > 0)
  );

  const sharedVideoRanges = sharedVideos
    .map(recording => (recording.stage_time_spans ?? []).map(span => [span.start_time, span.end_time] as TimeRange))
    .flat();

  return {
    sharedVideoSpeakers,
    mergedSharedVideoRanges: mergeWindows(sharedVideoRanges)
  };
};

export const buildSpeakerSpeakingSlots = ({
  speakerKey,
  transcriptSlots,
  fallbackSpeakingSlots,
  sharedVideoSubtractionContext
}: {
  speakerKey: string;
  transcriptSlots: TimeSpan[];
  fallbackSpeakingSlots: Record<string, number>;
  sharedVideoSubtractionContext: SharedVideoSubtractionContext;
}): Record<string, number> => {
  const transcriptRanges = transcriptSlots.map(slot => [slot.start, slot.end] as TimeRange);
  const { sharedVideoSpeakers, mergedSharedVideoRanges } = sharedVideoSubtractionContext;

  const effectiveRanges =
    sharedVideoSpeakers.has(speakerKey) || mergedSharedVideoRanges.length === 0
      ? transcriptRanges
      : subtractRanges(transcriptRanges, mergedSharedVideoRanges);

  const speakingSlotsFromTranscript = effectiveRanges.reduce(
    (slotMap, slot) => {
      slotMap[String(slot[0])] = slot[1];
      return slotMap;
    },
    {} as Record<string, number>
  );

  return Object.keys(speakingSlotsFromTranscript).length > 0 ? speakingSlotsFromTranscript : fallbackSpeakingSlots;
};
